/**
 * RelationshipChips — Block 2, Task B.
 *
 * Renders one chip per `UniversalSceneNode.relationships[]` entry of the
 * currently-selected node. Each chip shows the relationship `type` and the
 * TARGET node's label (resolved via `nodeById[targetObjectId]`). Clicking a
 * chip navigates to the target — the parent wires that to
 * `selectNode` + `selectByIndexNodeId` so the tree and camera follow.
 *
 * Domain-agnostic: the relationship `type` string is rendered verbatim; this
 * component never branches on domain (DESIGN_POLICY §5). When a target is not
 * present in `nodeById`, the chip falls back to showing the raw targetObjectId
 * but remains clickable (selection by id still works downstream).
 *
 * Source: scene-hierarchy-types §3 (SceneRelationship); 98-adoption-plan §Block2.
 */

import type { CSSProperties } from "react";
import { tokens } from "@/design";
import type { UniversalSceneNode } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface RelationshipChipsProps {
  /** The currently-selected node whose relationships are shown. */
  readonly node: UniversalSceneNode | null | undefined;
  /** objectId → node lookup, used to resolve each relationship target's label. */
  readonly nodeById: ReadonlyMap<string, UniversalSceneNode>;
  /**
   * Called when a chip is activated. Receives the target objectId and the
   * resolved target node (or `null` when the target is not in `nodeById`).
   */
  readonly onSelectTarget: (
    targetObjectId: string,
    target: UniversalSceneNode | null,
  ) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function RelationshipChips({
  node,
  nodeById,
  onSelectTarget,
}: RelationshipChipsProps) {
  const relationships = node?.relationships ?? [];
  if (relationships.length === 0) return null;

  const chipStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.space[1],
    maxWidth: "100%",
    height: tokens.density.rowDense,
    padding: `0 ${tokens.space[2]}px`,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.pill,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    cursor: "pointer",
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.xs,
    fontWeight: tokens.font.weight.medium,
    lineHeight: 1,
    whiteSpace: "nowrap",
  };

  return (
    <ul
      role="list"
      aria-label="Relationships"
      data-testid="relationship-chips"
      style={{
        listStyle: "none",
        margin: 0,
        padding: tokens.space[2],
        display: "flex",
        flexWrap: "wrap",
        gap: tokens.space[1],
      }}
    >
      {relationships.map((rel, index) => {
        const target = nodeById.get(rel.targetObjectId) ?? null;
        const targetLabel = target?.label ?? rel.targetObjectId;
        return (
          <li key={`${rel.type}:${rel.targetObjectId}:${index}`} style={{ maxWidth: "100%" }}>
            <button
              type="button"
              data-testid="relationship-chip"
              data-rel-type={rel.type}
              data-target-id={rel.targetObjectId}
              aria-label={`${rel.type} ${targetLabel}`}
              title={`${rel.type} → ${targetLabel}`}
              onClick={() => onSelectTarget(rel.targetObjectId, target)}
              style={chipStyle}
            >
              <span
                style={{
                  color: tokens.color.accent.primary,
                  fontWeight: tokens.font.weight.semibold,
                }}
              >
                {rel.type}
              </span>
              <span
                aria-hidden
                style={{ color: tokens.color.ink.muted }}
              >
                →
              </span>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {targetLabel}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
